import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Image, HardDrive, Users, Eye, TrendingUp, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatDate, formatBytes } from '@/lib/utils';

interface UserStats {
  totalUploads: number;
  totalViews: number;
  mostViewed: Array<{
    id: string;
    filename: string;
    shortCode: string;
    vanityUrl: string | null;
    views: number;
    mimeType: string;
  }>;
  recentUploads: Array<{
    id: string;
    filename: string;
    shortCode: string;
    createdAt: string;
    size: number;
  }>;
  fileTypeBreakdown: Array<{
    type: string;
    count: number;
  }>;
}

interface AdminStats {
  totalUsers: number;
  totalUploads: number;
  totalViews: number;
  totalStorage: number;
  mostActiveUsers: Array<{
    userId: string;
    username: string;
    uploadCount: number;
  }>;
  mostViewedGlobal: Array<{
    id: string;
    filename: string;
    shortCode: string;
    views: number;
    username: string;
  }>;
  recentActivity: Array<{
    id: string;
    filename: string;
    username: string;
    createdAt: string;
  }>;
}

export function Dashboard() {
  const { user } = useAuth();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch user stats
        const userData = await api.getUserStats();
        setUserStats(userData as UserStats);

        // Fetch admin stats if admin
        if (user?.role === 'admin') {
          const adminData = await api.getAdminStats();
          setAdminStats(adminData as AdminStats);
        }
      } catch (_error) {
        toast.error('Failed to load statistics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [user?.role]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">Loading...</div>
      </div>
    );
  }

  const imageCount = userStats?.fileTypeBreakdown.find((t) => t.type === 'Images')?.count || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="mt-2 text-muted-foreground">Welcome back, {user?.username}!</p>
          </div>

          {/* User Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Uploads</CardTitle>
                <Upload className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userStats?.totalUploads || 0}</div>
                <p className="text-xs text-muted-foreground">Files uploaded</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Views</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userStats?.totalViews || 0}</div>
                <p className="text-xs text-muted-foreground">Across all uploads</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Images</CardTitle>
                <Image className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{imageCount}</div>
                <p className="text-xs text-muted-foreground">Image files</p>
              </CardContent>
            </Card>

            {user?.role === 'admin' && adminStats && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{adminStats.totalUsers}</div>
                  <p className="text-xs text-muted-foreground">Registered users</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Most Viewed Uploads */}
            {userStats && userStats.mostViewed.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Most Viewed Uploads
                  </CardTitle>
                  <CardDescription>Your top performing files</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File</TableHead>
                        <TableHead className="text-right">Views</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userStats.mostViewed.map((upload) => (
                        <TableRow key={upload.id}>
                          <TableCell className="max-w-[200px] truncate font-medium">
                            {upload.filename}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Eye className="h-3 w-3" />
                              {upload.views}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* File Type Breakdown */}
            {userStats && userStats.fileTypeBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>File Type Breakdown</CardTitle>
                  <CardDescription>Distribution of your uploads</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {userStats.fileTypeBreakdown.map((fileType) => {
                      const percentage = (fileType.count / (userStats.totalUploads || 1)) * 100;
                      return (
                        <div key={fileType.type}>
                          <div className="mb-1 flex items-center justify-between text-sm">
                            <span className="font-medium">{fileType.type}</span>
                            <span className="text-muted-foreground">
                              {fileType.count} ({percentage.toFixed(0)}%)
                            </span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-muted">
                            <div
                              className="h-2 rounded-full bg-primary transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Admin Stats */}
          {user?.role === 'admin' && adminStats && (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">System Uploads</CardTitle>
                    <Upload className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{adminStats.totalUploads}</div>
                    <p className="text-xs text-muted-foreground">Across all users</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">System Views</CardTitle>
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{adminStats.totalViews}</div>
                    <p className="text-xs text-muted-foreground">Total file views</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
                    <HardDrive className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatBytes(adminStats.totalStorage)}</div>
                    <p className="text-xs text-muted-foreground">System-wide</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Most Active Users */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Most Active Users
                    </CardTitle>
                    <CardDescription>Top uploaders</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Username</TableHead>
                          <TableHead className="text-right">Uploads</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminStats.mostActiveUsers.map((user) => (
                          <TableRow key={user.userId}>
                            <TableCell className="font-medium">{user.username}</TableCell>
                            <TableCell className="text-right">{user.uploadCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                    <CardDescription>Latest uploads across all users</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>File</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminStats.recentActivity.map((activity) => (
                          <TableRow key={activity.id}>
                            <TableCell className="max-w-[150px] truncate font-medium">
                              {activity.filename}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {activity.username}
                            </TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground">
                              {formatDate(activity.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
